/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon.ts';
import { ContentTreeIconLoader as ContentTreeIcon } from '@confluence/icons/entry-points/ContentTreeIcon.ts';
import type { ContentTreeTypes } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import { includesValidContentTreeType } from '@confluence/icons/entry-points/contentTreeTypes.ts';
import { ManagedFolderClosedIcon } from '@confluence/smart-folder/entry-points/ManagedFolderClosedIcon.ts';
import { isSmartFolder } from '@confluence/smart-folder/entry-points/isSmartFolder.ts';

const styles = cssMap({
	pressable: {
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		textDecoration: 'none',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&:focus': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	searchResultRow: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.075'),
	},
	title: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		minWidth: '0px',
		flex: '1 1 auto',
		textAlign: 'left',
	},
	emojiWrapper: {
		justifyContent: 'center',
		flexShrink: 0,
		alignItems: 'center',
		marginRight: token('space.100'),
	},
	contentIconWrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
		marginRight: token('space.100'),
	},
});

type ContentTreeItem = {
	id: string;
	hasRestrictions?: boolean;
	hasInheritedRestrictions?: boolean;
	type?: string;
	subType?: string;
};

export type ContentSearchResultItemProps = {
	index?: number;
	title: string;
	onItemClick?: (id: string, index?: number) => void;
	item: ContentTreeItem;
	isCurrentPage?: boolean;
	displayEmoji?: string;
};

// Emoji wrapper components
const EmojiWrapper = ({ children }: { children?: React.ReactNode }) => {
	return (
		<Inline as="span" xcss={styles.emojiWrapper}>
			{children}
		</Inline>
	);
};

// Icon component that handles both content tree icons and emojis
const EmojiOrContentIcon = ({
	isContentTreeIconVisible,
	contentType,
	subType,
	iconColor,
	displayEmoji,
}: {
	isContentTreeIconVisible: boolean;
	contentType: string;
	subType?: string;
	iconColor?: string;
	displayEmoji?: string;
}) => {
	const isManagedFolder = isSmartFolder({ type: contentType, subType });

	if (displayEmoji && isValidEmojiOrFavicon(displayEmoji, contentType)) {
		return (
			<LazyEmojiComponentLoader
				emoji={displayEmoji}
				height={16}
				context="spaceViews"
				wrapper={EmojiWrapper}
				contentType={contentType}
			/>
		);
	} else if (isContentTreeIconVisible) {
		return (
			<Box as="span" xcss={styles.contentIconWrapper}>
				{isManagedFolder ? (
					<ManagedFolderClosedIcon label="" size="small" color={iconColor} />
				) : (
					<ContentTreeIcon
						type={contentType as ContentTreeTypes}
						subType={subType}
						label=""
						color={iconColor}
					/>
				)}
			</Box>
		);
	} else {
		return null;
	}
};

export const ContentSearchResultItem = ({
	index,
	title,
	item,
	onItemClick,
	isCurrentPage,
	displayEmoji,
}: ContentSearchResultItemProps): JSX.Element => {
	// Will add these in a followup styling the result rows
	void isCurrentPage;

	// Determine content type and icon visibility
	const contentType = item.type || 'page';
	const isContentTreeIconVisible = contentType && includesValidContentTreeType(contentType);
	const isLiveDoc = contentType === 'page' && item.subType === 'live';
	const iconContentType = isLiveDoc ? 'liveDoc' : contentType;

	return (
		<Pressable
			xcss={styles.pressable}
			onClick={() => onItemClick?.(item.id, index)}
			testId="content-search-result-item"
			role="listitem"
		>
			<Inline xcss={styles.searchResultRow} alignBlock="center">
				<EmojiOrContentIcon
					isContentTreeIconVisible={!!isContentTreeIconVisible}
					contentType={iconContentType}
					subType={item.subType}
					displayEmoji={displayEmoji}
				/>
				<Box xcss={styles.title}>
					<Text>{title}</Text>
				</Box>
			</Inline>
		</Pressable>
	);
};
